import { useMemo } from 'react';
import {
  CalendarClock,
  Clock3,
  Rocket,
  Wand2,
  ShieldAlert,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { MapleMarketApp } from '@/data/mapleMarketApps';
import type { AppDetailTab } from '@/components/maple-market/admin/AppDetailPanel';

interface EventsPanelProps {
  products: MapleMarketApp[];
  onOpenProduct: (productId: string, initialTab?: AppDetailTab) => void;
}

function getReleaseTimestamp(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
}

function formatDate(value: string | null | undefined) {
  if (!value) {
    return 'No date set';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Invalid date';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

export default function EventsPanel({ products, onOpenProduct }: EventsPanelProps) {
  const now = Date.now();
  const upcomingThreshold = now - 1000 * 60 * 60 * 24 * 30;

  const upcomingReleases = useMemo(
    () =>
      products
        .filter((product) => {
          const releaseTime = getReleaseTimestamp(product.releaseDate);
          return releaseTime !== null && releaseTime >= upcomingThreshold;
        })
        .sort((left, right) => (getReleaseTimestamp(left.releaseDate) ?? 0) - (getReleaseTimestamp(right.releaseDate) ?? 0)),
    [products, upcomingThreshold]
  );

  const pendingPublishRequests = useMemo(
    () => products.filter((product) => product.publishRequestStatus === 'pending').slice(0, 8),
    [products],
  );

  const recentLaunches = useMemo(
    () =>
      products
        .filter((product) => {
          const releaseTime = getReleaseTimestamp(product.releaseDate);
          return releaseTime !== null && releaseTime < now;
        })
        .sort((left, right) => (getReleaseTimestamp(right.releaseDate) ?? 0) - (getReleaseTimestamp(left.releaseDate) ?? 0))
        .slice(0, 5),
    [products, now]
  );

  const unscheduledProducts = useMemo(
    () => products.filter((product) => !product.releaseDate).slice(0, 6),
    [products]
  );

  const next30DaysCount = useMemo(
    () =>
      products.filter((product) => {
        const releaseTime = getReleaseTimestamp(product.releaseDate);
        return releaseTime !== null && releaseTime >= now && releaseTime <= now + 1000 * 60 * 60 * 24 * 30;
      }).length,
    [products, now]
  );

  return (
    <div className="space-y-6 p-6 lg:p-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Publishing queue</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Track pending first-time publication requests alongside upcoming launches and release scheduling.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Pending requests</CardDescription>
            <CardTitle className="mt-2 text-3xl">{pendingPublishRequests.length}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">Awaiting admin review.</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Scheduled releases</CardDescription>
            <CardTitle className="mt-2 text-3xl">{upcomingReleases.length}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">Products with a live or upcoming release date.</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Next 30 days</CardDescription>
            <CardTitle className="mt-2 text-3xl">{next30DaysCount}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">Releases landing in the next month.</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Unscheduled products</CardDescription>
            <CardTitle className="mt-2 text-3xl">{unscheduledProducts.length}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">Products that still need a release date.</CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Pending publication requests</CardTitle>
            <CardDescription>Approve or reject first-time publication requests from this queue.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingPublishRequests.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                No first-time publish requests are waiting right now.
              </div>
            ) : (
              pendingPublishRequests.map((product) => (
                <div key={product.id} className="flex items-center justify-between gap-4 rounded-xl border border-border p-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="rounded-xl bg-primary/10 p-2 text-primary">
                      <ShieldAlert className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate font-medium text-foreground">{product.name}</div>
                      <div className="text-sm text-muted-foreground">Requested {product.publishRequestedAt ? new Date(product.publishRequestedAt).toLocaleString() : 'recently'}</div>
                    </div>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={() => onOpenProduct(product.id, 'publish')}>
                    Review
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Upcoming & active releases</CardTitle>
            <CardDescription>Open a product directly in Publishing to adjust release timing.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {upcomingReleases.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                No upcoming release events are scheduled right now.
              </div>
            ) : (
              upcomingReleases.slice(0, 8).map((product) => (
                <div key={product.id} className="flex items-center justify-between gap-4 rounded-xl border border-border p-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="rounded-xl bg-primary/10 p-2 text-primary">
                      <CalendarClock className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate font-medium text-foreground">{product.name}</div>
                      <div className="text-sm text-muted-foreground">{formatDate(product.releaseDate)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {product.featured && <Badge variant="secondary">Featured</Badge>}
                    <Button type="button" size="sm" variant="outline" onClick={() => onOpenProduct(product.id, 'publish')}>
                      Open publish
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Recent launches</CardTitle>
            <CardDescription>Recently released products in your current admin scope.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentLaunches.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                No recent launches are available yet.
              </div>
            ) : (
              recentLaunches.map((product) => (
                <div key={product.id} className="flex items-center justify-between gap-4 rounded-xl border border-border p-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="rounded-xl bg-primary/10 p-2 text-primary">
                      <Rocket className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate font-medium text-foreground">{product.name}</div>
                      <div className="text-sm text-muted-foreground">Released {formatDate(product.releaseDate)}</div>
                    </div>
                  </div>
                  <Button type="button" size="sm" variant="ghost" onClick={() => onOpenProduct(product.id, 'basic')}>
                    View app
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Launch guidance</CardTitle>
            <CardDescription>Use publishing workflow data to coordinate launches and approvals.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <div className="flex items-start gap-3 rounded-xl border border-border p-4">
              <Wand2 className="mt-0.5 h-4 w-4 text-primary" />
              <p>First-time publish requests stay hidden until an admin approves them.</p>
            </div>
            <div className="flex items-start gap-3 rounded-xl border border-border p-4">
              <Rocket className="mt-0.5 h-4 w-4 text-primary" />
              <p>Use release dates and featured placement together when you want a launch to line up with store promotion.</p>
            </div>
            <div className="flex items-start gap-3 rounded-xl border border-border p-4">
              <Clock3 className="mt-0.5 h-4 w-4 text-primary" />
              <p>Products without release dates stay out of launch planning and should be scheduled before publication.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
